import React, { useState } from 'react';

import { Box, Button, FormControl, InputLabel, MenuItem, Select } from '@mui/material';
import { OrdTransferList } from '@ragu-components/ord-components';
import { Resources } from '@ragu-components/ord-resources';
import { dataSchema } from '@ragu-components/ord-types';
import { useGetRecordRepresentation, useNotify, useRefresh, useTranslate } from 'react-admin';

import { useUserRecordingConfsData } from './hooks/useUserRecordingConfsData';
import { useUserRecordingConfsOperations } from './hooks/useUserRecordingConfsOperations';

const RECORDING_TYPES = dataSchema.UserCallRecordingConfCreate.properties.type.enum;

const hasListChanged = (initial: string[], current: string[]): boolean => {
  if (initial.length !== current.length) return true;
  const set = new Set(initial);
  return current.some((id) => !set.has(id));
};

export const AssignUserRecordingConfsForm = ({
  confId,
  onUpdate = (): void => {},
}: {
  confId: string;
  onUpdate?: () => void;
}): React.ReactElement => {
  const translate = useTranslate();
  const refresh = useRefresh();
  const notify = useNotify();

  const { userAssignmentMap, assignedUserIds, loaded } = useUserRecordingConfsData(confId);
  const { createAssignments, deleteAssignments } = useUserRecordingConfsOperations(confId);

  const [currentUserIds, setCurrentUserIds] = useState<string[]>([]);
  const [type, setType] = useState<string>(RECORDING_TYPES[0]);
  const [updateDisabled, setUpdateDisabled] = useState(true);
  const [saving, setSaving] = useState(false);

  const initialUserIds = React.useRef<string[]>([]);

  const userRepresentation = useGetRecordRepresentation(Resources.users);

  React.useEffect(() => {
    if (loaded) {
      initialUserIds.current = assignedUserIds;
      setCurrentUserIds(assignedUserIds);
      setUpdateDisabled(true);
    }
  }, [loaded, assignedUserIds]);

  const handleUsersChange = (selectedIds: string[]): void => {
    setCurrentUserIds(selectedIds);
    setUpdateDisabled(!hasListChanged(initialUserIds.current, selectedIds));
  };

  const handleSave = (): void => {
    setSaving(true);

    const currentSet = new Set(currentUserIds);
    const toAdd = currentUserIds.filter((id) => !userAssignmentMap.has(id));
    const toRemove = Array.from(userAssignmentMap.keys()).filter((id) => !currentSet.has(id));
    const assignmentIdsToDelete = toRemove
      .map((id) => userAssignmentMap.get(id))
      .filter((id): id is string => id !== undefined);

    void (async () => {
      const [addResult, deleteResult] = await Promise.all([
        createAssignments(toAdd, type),
        deleteAssignments(assignmentIdsToDelete),
      ]);

      const totalFailed = addResult.failed.length + deleteResult.failed.length;
      const totalSuccessful = addResult.successful.length + deleteResult.successful.length;
      const allErrors = new Set([...addResult.errors, ...deleteResult.errors]);

      if (totalFailed > 0) {
        const errorsMessage = allErrors.size > 0 ? ` (${Array.from(allErrors).join(', ')})` : '';
        const msg =
          totalSuccessful > 0
            ? `${translate('ra.notification.updated', { smart_count: totalSuccessful })}. ${translate('ord.httpErrors.updateManyFailed')}${errorsMessage}`
            : `${translate('ord.httpErrors.updateManyFailed')}${errorsMessage}`;
        notify(msg, { type: totalSuccessful > 0 ? 'warning' : 'error', messageArgs: { _: msg } });
      } else {
        notify('ra.notification.updated', {
          type: 'success',
          messageArgs: { smart_count: totalSuccessful },
        });
      }

      setSaving(false);
      refresh();
      onUpdate();
    })();
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, padding: 2, overflow: 'auto' }}>
      {loaded ? (
        <>
          <OrdTransferList
            key={JSON.stringify(currentUserIds)}
            selectedIds={currentUserIds}
            fiqlArgument='username'
            resource={Resources.users}
            onChange={handleUsersChange}
            optionText={userRepresentation}
          />
          <FormControl variant='outlined' size='small'>
            <InputLabel id='recording-type-label'>
              {translate('resources.user_call_recording_confs.fields.type')}
            </InputLabel>
            <Select
              labelId='recording-type-label'
              value={type}
              label={translate('resources.user_call_recording_confs.fields.type')}
              onChange={(e) => {
                setType(e.target.value);
              }}
            >
              {RECORDING_TYPES.map((t) => (
                <MenuItem key={t} value={t}>
                  {translate(`resources.call_recording_confs.fields.types.${t}`, { _: t })}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button onClick={handleSave} disabled={updateDisabled || saving} variant='contained'>
            {translate('ra.action.save')}
          </Button>
        </>
      ) : null}
    </Box>
  );
};
